import type { Document } from '@contentful/rich-text-types';
import { type ZodObject, type ZodRawShape, z } from 'zod';

import { maybe } from './maybe';

type ContentfulDate = `${number}-${number}-${number}T${number}:${number}:${number}Z`;
const contentfulDate = z.custom<ContentfulDate>(v => typeof v === 'string');

function linkRef<T extends string>(linkType: T) {
    return z.object({ sys: z.object({ type: z.literal('Link'), linkType: z.literal(linkType), id: z.string() }) });
}

const taxonomyConceptLink = z.object({
    sys: z.object({
        type: z.literal('Link'),
        linkType: z.literal('TaxonomyConcept'),
        id: z.string(),
    }),
});

const assetFile = z.object({
    url: z.string(),
    details: z.object({
        size: z.number().int(),
        image: z.object({ width: z.number(), height: z.number() }).optional(),
    }),
    fileName: z.string(),
    contentType: z.string(),
});

export const asset = maybe(
    z.object({
        sys: z.object({
            id: z.string(),
            type: z.literal('Asset'),
            revision: z.number().int(),
            createdAt: contentfulDate,
            updatedAt: contentfulDate,
            locale: z.string().optional(),
            space: linkRef('Space'),
            environment: linkRef('Environment'),
        }),
        metadata: z.object({
            tags: z.array(linkRef('Tag')),
            concepts: z.array(taxonomyConceptLink).optional(),
        }),
        fields: z.object({
            title: z.string().optional(),
            description: z.string().optional(),
            file: assetFile.optional(),
        }),
    })
);

export const richText = z.custom<Document>(
    v =>
        typeof v === 'object' && v !== null && 'nodeType' in v && (v as Record<string, unknown>).nodeType === 'document'
);

// Cross-space reference (ResourceLink field type).
// The urn follows the pattern: crn:contentful:::content:spaces/<id>/entries/<id>
export const resourceLink = z.object({
    sys: z.object({
        type: z.literal('ResourceLink'),
        linkType: z.string(),
        urn: z.string(),
    }),
});

export interface EntryOptions<TagId extends string, Locale extends string> {
    tagIds?: readonly [TagId, ...TagId[]];
    locales?: readonly [Locale, ...Locale[]];
}

export function entry<
    T extends string,
    S extends ZodRawShape,
    TagId extends string = string,
    Locale extends string = string,
>(contentType: T, fields: ZodObject<S>, options: EntryOptions<TagId, Locale> = {}) {
    const tagId = options.tagIds ? z.enum([...options.tagIds] as [TagId, ...TagId[]]) : z.string();
    const locale = options.locales
        ? z.enum([...options.locales] as [Locale, ...Locale[]]).optional()
        : z.string().optional();

    return maybe(
        z.object({
            sys: z.object({
                id: z.string(),
                type: z.literal('Entry'),
                revision: z.number().int(),
                // Never-published entries returned by the Preview API have no published version.
                publishedVersion: z.number().int().optional(),
                createdAt: contentfulDate,
                updatedAt: contentfulDate,
                locale,
                space: linkRef('Space'),
                environment: linkRef('Environment'),
                contentType: z.object({
                    sys: z.object({
                        type: z.literal('Link'),
                        linkType: z.literal('ContentType'),
                        id: z.literal(contentType),
                    }),
                }),
            }),
            metadata: z.object({
                tags: z.array(
                    z.object({
                        sys: z.object({
                            type: z.literal('Link'),
                            linkType: z.literal('Tag'),
                            id: tagId,
                        }),
                    })
                ),
                concepts: z
                    .array(
                        z.object({
                            sys: z.object({
                                type: z.literal('Link'),
                                linkType: z.literal('TaxonomyConcept'),
                                id: z.string(),
                            }),
                        })
                    )
                    .optional(),
            }),
            fields,
        })
    );
}
